import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSignIn, useUser } from '@clerk/react';
import usePageTitle from '../hooks/usePageTitle';
import './Login.css';

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 10 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Login = () => {
  usePageTitle('Sign In');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useSignIn();
  const { isSignedIn, isLoaded: clerkLoaded } = useUser();

  // Redirect if already signed in
  if (clerkLoaded && isSignedIn) {
    return <Navigate to="/etl" replace />;
  }

  const handleGoogleSignIn = async () => {
    if (!signIn) return;
    setError('');
    setLoading(true);

    try {
      await signIn.sso({
        strategy: 'oauth_google',
        redirectUrl: '/etl',
        redirectCallbackUrl: '/sso-callback',
      });
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('Failed to start Google sign-in. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="bg-shape bg-shape-1"></div>
        <div className="bg-shape bg-shape-2"></div>
      </div>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-brand">
              <div className="brand-logo">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" />
                </svg>
              </div>
              <h1 className="brand-name">Flowy</h1>
            </div>
            <p className="login-subtitle">Sign in to manage your data pipelines</p>
          </div>

          {error && (
            <div className="error-message">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 5V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
              </svg>
              {error}
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            disabled={loading || !signIn}
            className="login-button google-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            {loading ? (
              <span className="button-loader">
                <svg className="spinner" viewBox="0 0 50 50">
                  <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                </svg>
                Redirecting...
              </span>
            ) : (
              <>
                <GoogleIcon />
                Sign in with Google
              </>
            )}
          </button>
        </div>
        <div className="login-footer">
          &copy; 2026 LampTwist. Enterprise ETL Solutions.
        </div>
      </div>
    </div>
  );
};

export default Login;
